import { Polygon, Point } from 'ol/geom'
import { getCenter } from 'ol/extent'
import { getRoot } from '/@/root'
import { toLonLat } from 'ol/proj.js'
import { FlightParams } from '/@/types/flight-params'
import { FlightWH, RouteBearing, RoutePoint } from '/@/types/route-bearing'
import { FlightCamera } from '/@/types/flight-camera'
import { computed } from 'vue'
import { useMyStore } from '/@/store'

export function UseLawnmower () {
  const root = getRoot()
  const store = useMyStore()

  // --- Utilities ---
  function getAltitudeFromCoordinate (coord:number[]):number | null {
    const layerPixel = root?.$map.getPixelFromCoordinate(coord)
    const layerData = root?.$demLayer.getData(layerPixel)
    return layerData ? layerData[0] : null
  }

  function calculateGsd (
    altitude:number,
    camera: FlightCamera,
    flightParams: FlightParams,
    polygon: Polygon,
    refHeight: number
  ):FlightWH {
    const minAltitude = altitude
    /// TODO: provide range of GSD or variable GSD?
    // let minAltitude:number = 0
    // if (flightParams.surfaceFollowModeEnable) {
    //   minAltitude = altitude
    // } else {
    //   const coords:number[][] = polygon.getCoordinates()[0]
    //   let min = Infinity
    //   let alt:number | null
    //   for (const coord of coords) {
    //     alt = getAltitudeFromCoordinate(coord)
    //     if (alt !== null) {
    //       if (min > alt) {
    //         min = alt
    //       }
    //     }
    //   }
    //   const origin = getCenter(polygon.getExtent())
    //   alt = getAltitudeFromCoordinate(origin)
    //   if (alt !== null) {
    //     if (min > alt) {
    //       min = alt
    //     }
    //   }
    //   minAltitude = altitude - min + refHeight
    // }
    const gsdW = (minAltitude * camera.sensorWidth) / (camera.focalLength * camera.imageWidth)
    const gsdH = (minAltitude * camera.sensorHeight) / (camera.focalLength * camera.imageHeight)
    return { width: gsdW, height: gsdH }
  }

  // Ground footprint based on GSD
  function calculateFootprint (gsd: FlightWH, camera: FlightCamera, flightParams:FlightParams): FlightWH {
    const f = {
      width: camera.imageWidth * gsd.width * (100 - flightParams.sideOverlap) / 100,
      height: camera.imageHeight * gsd.height * (100 - flightParams.frontOverlap) / 100
    }
    return f
  }

  // // function bufferPolygon(polygon: Polygon, margin: number): Polygon {
  // //   const reader = new jsts.io.OL3Parser();
  // //   const jstsPolygon = reader.read(polygon); // Convert OL polygon to JSTS geometry
  // //   const buffered = jstsPolygon.buffer(margin); // margin in same units as coordinates
  // //   return reader.write(buffered) as Polygon; // Convert back to OL polygon
  // // }

  // Rotate a point around center by angleDeg clockwise
  function rotatePoint (coord:number[], center:number[], angleDeg:number) {
    const angle = angleDeg * Math.PI / 180
    const cos = Math.cos(angle); const sin = Math.sin(angle)
    const dx = coord[0] - center[0]; const dy = coord[1] - center[1]
    return [center[0] + dx * cos - dy * sin, center[1] + dx * sin + dy * cos]
  }

  // Rotate all points
  function rotateRoutePoints (
    polygon: Polygon,
    points: Point[],
    angleDeg:number
  ):number[][] {
    const extent = polygon.getExtent()
    const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]
    return points.map(p => rotatePoint(p.getCoordinates(), center, angleDeg))
  }

  // Calculate bearing in degrees from p1 to p2
  function calculateBearing (p1: number[], p2: number[]) {
    const lon1 = p1[0] * Math.PI / 180
    const lat1 = p1[1] * Math.PI / 180
    const lon2 = p2[0] * Math.PI / 180
    const lat2 = p2[1] * Math.PI / 180

    const y = Math.sin(lon2 - lon1) * Math.cos(lat2)
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)
    const brng = Math.atan2(y, x) * 180 / Math.PI
    return (brng + 360) % 360
  }

  // // Generate lawnmower inside polygon
  // function generateLawnmower (polygon, footprint) {
  //   const extent = polygon.getExtent()
  //   const minX = extent[0]
  //   const minY = extent[1]
  //   const maxX = extent[2]
  //   const maxY = extent[3]
  //   const points = []
  //   let y = minY; let direction = 1
  //   while (y <= maxY) {
  //     if (direction === 1) {
  //       for (let x = minX; x <= maxX; x += footprint.width) {
  //         if (polygon.intersectsCoordinate([x, y])) points.push([x, y])
  //       }
  //     } else {
  //       for (let x = maxX; x >= minX; x -= footprint.width) {
  //         if (polygon.intersectsCoordinate([x, y])) points.push([x, y])
  //       }
  //     }
  //     y += footprint.height
  //     direction *= -1
  //   }
  //   return points
  // }

  // // Generate points along polygon edges
  // // function generateEdgePoints (polygon, spacing) {
  // //   const coords = polygon.getCoordinates()[0]
  // //   const edgePoints = []
  // //   for (let i = 0; i < coords.length - 1; i++) {
  // //     const [x1, y1] = coords[i]; const [x2, y2] = coords[i + 1]
  // //     const dx = x2 - x1; const dy = y2 - y1
  // //     const dist = Math.hypot(dx, dy)
  // //     const segments = Math.max(Math.floor(dist / spacing), 1)
  // //     for (let j = 0; j <= segments; j++) {
  // //       const t = j / segments
  // //       edgePoints.push([x1 + t * dx, y1 + t * dy])
  // //     }
  // //   }
  // //   return edgePoints
  // // }

  // // Combine interior + edge points
  // function generateFullRoute (polygon, footprint) {
  //   // const extent = polygon.getExtent()

  //   // const cx = (extent[0] + extent[2]) / 2
  //   // const cy = (extent[1] + extent[3]) / 2

  //   // Scale polygon by 5%
  //   // polygon.scale(1.1, 1.1, [cx, cy])
  //   // optional anchor (defaults to geometry center if omitted)
  //   const boundary = polygon.clone()
  //   const anchor = boundary.getInteriorPoint().getCoordinates()
  //   // apply scaling
  //   boundary.scale(1.2, 1.2, anchor)

  //   const lawnmower = generateLawnmower(boundary, footprint)
  //   // const edges = generateEdgePoints(polygon, Math.min(footprint.width, footprint.height))
  //   const allPoints = [...lawnmower]
  //   // for (const p of edges) {
  //   //   if (!allPoints.some(pt => Math.hypot(pt[0] - p[0], pt[1] - p[1]) < 1e-6)) allPoints.push(p)
  //   // }
  //   return allPoints
  // }

  function computePitch (p1:number[], p2:number[]) {
    const dz = p2[2] - p1[2]
    const ds = Math.hypot(p2[0] - p1[0], p2[1] - p1[1])
    const slope = ds > 0 ? dz / ds : 0
    return Math.atan(slope) * (180 / Math.PI)
  }

  /**
   * Generate safe 3D waypoints along a straight line from start to end
   * @param {Array} start - [x, y, z_start]  (map coordinates + start altitude)
   * @param {Array} end   - [x, y, z_end]
   * @param {Object} root - your root object that has $map and $demLayer
   * @param {number} clearance - meters above terrain (recommended 5–10)
   * @param {number} samplingInterval - meters along the path (2-5 recommended)
   * @returns {Object} { waypoints: [[x,y,z], ...], pitchDeg: [...], maxTerrain: number, ... }
  * - 3D ball clearance
  * - Only adds points when needed
  * - Reduces waypoints to minimum (same attitude / straight segments)
  */
  function generateSafeDronePath (
    start:number[],
    end:number[],
    root:any, clearance = 10.0, samplingInterval = 3.0, horizRadius:number = 0) {
    const [x1, y1, z1] = start
    const [x2, y2, z2] = end
    const horizontalRadius = horizRadius !== 0 ? horizRadius : clearance * 0.75

    const dx = x2 - x1
    const dy = y2 - y1
    const totalDist = Math.hypot(dx, dy)

    if (totalDist < 10) return { waypoints: [start], pitchDeg: [0], distance: totalDist, isDirect: true }

    // ==================== 1. Dense Sampling with 3D Clearance ====================
    const numSamples = Math.ceil(totalDist / samplingInterval) + 1
    const densePoints = []
    const rawWaypoints = []
    let needsExtra = false
    let maxTerrain = -Infinity

    const searchSteps = 8

    for (let i = 0; i < numSamples; i++) {
      const t = i / (numSamples - 1)
      const x = x1 + t * dx
      const y = y1 + t * dy

      // Max terrain in horizontal radius (3D ball)
      let maxTerrainInRadius = 0
      for (let a = 0; a < searchSteps; a++) {
        const angle = (a / searchSteps) * Math.PI * 2
        for (let r = 0; r <= horizontalRadius; r += horizontalRadius / 3) {
          const tx = x + Math.cos(angle) * r
          const ty = y + Math.sin(angle) * r
          const alt = getAltitudeFromCoordinate([tx, ty])
          if (alt !== null) {
            maxTerrainInRadius = Math.max(maxTerrainInRadius, alt)
          }
        }
      }

      const zLinear = z1 + t * (z2 - z1)
      const zMinSafe = maxTerrainInRadius + clearance
      const zSafe = Math.max(zLinear, zMinSafe)

      rawWaypoints.push({
        // x,
        // y,
        // zLinear,
        zMinSafe
        // needsRaise: zLinear < zMinSafe // ← This detects "wall"
      })

      if (zLinear < zMinSafe) needsExtra = true
      maxTerrain = Math.max(maxTerrain, maxTerrainInRadius)

      densePoints.push({ x, y, z: zSafe, terrainMax: maxTerrainInRadius })
    }

    // ==================== 2. Direct Path if Safe ====================
    if (!needsExtra) {
      return {
        waypoints: [start, end],
        pitchDeg: [0, 0],
        distance: totalDist,
        minClearance: Number((z1 - maxTerrain).toFixed(2)),
        maxTerrain: Number(maxTerrain.toFixed(1)),
        isDirect: true,
        waypointsReduced: 2
      }
    }

    // ==================== 3. Reduce Waypoints (Colinear + Same Attitude) ====================
    let safeWaypoints = densePoints.map(p => [p.x, p.y, p.z])

    // Force start and end
    safeWaypoints[0][2] = z1
    safeWaypoints[safeWaypoints.length - 1][2] = z2

    // Optional light smoothing (keeps it reasonable)
    const windowSize = Math.max(5, Math.floor(numSamples * 0.07))
    for (let i = 1; i < safeWaypoints.length - 1; i++) {
      let maxNeeded = safeWaypoints[i][2]
      for (let j = i; j < Math.min(safeWaypoints.length, i + windowSize); j++) {
        maxNeeded = Math.max(maxNeeded, rawWaypoints[j].zMinSafe)
      }
      safeWaypoints[i][2] = Math.max(safeWaypoints[i][2], maxNeeded)
    }

    // Apply simplification
    safeWaypoints = simplify3DPath(safeWaypoints, 0.6) // tolerance in meters

    // Compute final pitch
    const pitchDeg = []
    for (let i = 0; i < safeWaypoints.length - 1; i++) {
      const dz = safeWaypoints[i + 1][2] - safeWaypoints[i][2]
      const ds = Math.hypot(
        safeWaypoints[i + 1][0] - safeWaypoints[i][0],
        safeWaypoints[i + 1][1] - safeWaypoints[i][1]
      )
      const pitch = ds > 0 ? Math.atan(dz / ds) * (180 / Math.PI) : 0
      pitchDeg.push(Number(pitch.toFixed(2)))
    }
    pitchDeg.push(pitchDeg[pitchDeg.length - 1] || 0)
    const lonlats = []
    // Final minClearance
    let minClearance = Infinity
    for (let i = 0; i < safeWaypoints.length; i++) {
      // Approximate clearance (conservative)
      const idx = Math.floor((i / (safeWaypoints.length - 1)) * (densePoints.length - 1))
      const clearanceHere = safeWaypoints[i][2] - densePoints[Math.min(idx, densePoints.length - 1)].terrainMax
      minClearance = Math.min(minClearance, clearanceHere)
      lonlats.push(toLonLat([safeWaypoints[i][0], safeWaypoints[i][1]]))
    }

    return {
      waypoints: safeWaypoints,
      lonlats: lonlats,
      pitchDeg: pitchDeg,
      distance: totalDist,
      minClearance: Number(minClearance.toFixed(2)),
      maxTerrain: Number(maxTerrain.toFixed(1)),
      isDirect: false,
      waypointsReduced: safeWaypoints.length,
      originalSamples: densePoints.length
    }
  }

  // ==================== Waypoint Simplification (Key Function) ====================
  function simplify3DPath (points, tolerance = 0.5) {
    if (points.length <= 2) return points

    const result = [points[0]]
    let last = 0

    for (let i = 1; i < points.length - 1; i++) {
      const dx1 = points[i][0] - points[last][0]
      const dy1 = points[i][1] - points[last][1]
      const dz1 = points[i][2] - points[last][2]
      const dist1 = Math.hypot(dx1, dy1, dz1)

      const dx2 = points[i + 1][0] - points[last][0]
      const dy2 = points[i + 1][1] - points[last][1]
      const dz2 = points[i + 1][2] - points[last][2]
      const dist2 = Math.hypot(dx2, dy2, dz2)

      // Check if point i is almost on the line from last → i+1
      const cross = Math.hypot(
        dy1 * dz2 - dz1 * dy2,
        dz1 * dx2 - dx1 * dz2,
        dx1 * dy2 - dy1 * dx2
      )

      const deviation = dist1 > 0 ? cross / dist2 : 0

      if (deviation > tolerance) {
        result.push(points[i])
        last = i
      }
    }

    result.push(points[points.length - 1])
    return result
  }
  // function generateSafeDronePath (start, end, root, clearance = 8.0, samplingInterval = 3.0) {
  //   const [x1, y1, z1] = start
  //   const [x2, y2, z2] = end

  //   const dx = x2 - x1
  //   const dy = y2 - y1
  //   const totalDist = Math.hypot(dx, dy)

  //   if (totalDist === 0) {
  //     return { waypoints: [start], pitchDeg: [0], distance: 0, minClearance: Infinity, maxTerrain: z1 }
  //   }

  //   const numSamples = Math.ceil(totalDist / samplingInterval) + 1
  //   const zTerrainList = []
  //   let maxTerrain = -Infinity
  //   let minClearance = Infinity // ← This is what we're tracking

  //   const rawWaypoints = []
  //   let needsExtraPoints = false

  //   // First pass: check if we actually need extra waypoints
  //   for (let i = 0; i < numSamples; i++) {
  //     const t = i / (numSamples - 1)
  //     const x = x1 + t * dx
  //     const y = y1 + t * dy

  //     const pixel = root?.$map.getPixelFromCoordinate([x, y])
  //     let terrainAlt = 0
  //     if (pixel && root?.$demLayer) {
  //       const data = root?.$demLayer.getData(pixel)
  //       terrainAlt = data && data.length > 0 ? Number(data[0]) || 0 : 0
  //     }

  //     const zLinear = z1 + t * (z2 - z1)
  //     const zMinSafe = terrainAlt + clearance

  //     zTerrainList.push(terrainAlt)
  //     maxTerrain = Math.max(maxTerrain, terrainAlt)
  //     const actualClearance = Math.max(zLinear, zMinSafe) - terrainAlt
  //     minClearance = Math.min(minClearance, actualClearance)

  //     rawWaypoints.push({
  //       x,
  //       y,
  //       zLinear,
  //       zMinSafe,
  //       terrainAlt,
  //       needsRaise: zLinear < zMinSafe // ← This detects "wall"
  //     })

  //     if (zLinear < zMinSafe) {
  //       console.log('zLinear: %d, zMinSafe: %d', zLinear, zMinSafe)
  //       needsExtraPoints = true
  //     }
  //   }

  //   // Case 1: No obstacle → just return start + end (clean!)
  //   if (!needsExtraPoints) {
  //     // console.log('✓ No obstacles detected. Using direct straight path.')
  //     const pitch = computePitch(start, end)
  //     return {
  //       waypoints: [start, end],
  //       pitchDeg: [pitch, 0], // or compute real pitch if you want
  //       distance: totalDist,
  //       minClearance: Number((z1 - maxTerrain).toFixed(2)), // rough
  //       maxTerrain: Number(maxTerrain.toFixed(1)),
  //       isDirect: true
  //     }
  //   }

  //   const lonlats = []
  //   const safeWaypoints = []

  //   for (let i = 0; i < rawWaypoints.length; i++) {
  //     const pt = rawWaypoints[i]
  //     const zSafe = Math.max(pt.zLinear, pt.zMinSafe)
  //     safeWaypoints.push([pt.x, pt.y, zSafe])
  //     lonlats.push(toLonLat([pt.x, pt.y]))
  //   }

  //   // Force start and end
  //   safeWaypoints[0][2] = z1
  //   safeWaypoints[safeWaypoints.length - 1][2] = z2

  //   // Optional light smoothing (keeps it reasonable)
  //   const windowSize = Math.max(5, Math.floor(numSamples * 0.07))
  //   for (let i = 1; i < safeWaypoints.length - 1; i++) {
  //     let maxNeeded = safeWaypoints[i][2]
  //     for (let j = i; j < Math.min(safeWaypoints.length, i + windowSize); j++) {
  //       maxNeeded = Math.max(maxNeeded, rawWaypoints[j].zMinSafe)
  //     }
  //     safeWaypoints[i][2] = Math.max(safeWaypoints[i][2], maxNeeded)
  //   }

  //   // const indicesToRemove:number[] = []
  //   let checkpointz = z1
  //   const filteredSafeWaypoints = []
  //   let filteredSafeWaypointsIdx = 1
  //   filteredSafeWaypoints.push([safeWaypoints[0][0], safeWaypoints[0][1], z1])
  //   for (let i = 1; i < safeWaypoints.length - 1; i++) {
  //     const nextZ = safeWaypoints[i][2]
  //     if (Math.abs(checkpointz - nextZ) <= 0.6) {
  //       if (nextZ > checkpointz) {
  //         filteredSafeWaypoints[filteredSafeWaypointsIdx - 1][2] = nextZ
  //         checkpointz = nextZ
  //       }
  //     } else {
  //       checkpointz = safeWaypoints[i][2]
  //       filteredSafeWaypoints.push(safeWaypoints[i])
  //       filteredSafeWaypointsIdx++
  //     }
  //   }

  //   // const removeSet = new Set(indicesToRemove)
  //   // const filteredSafeWaypoints = safeWaypoints.filter((_, i) => !removeSet.has(i))

  //   // filteredSafeWaypoints[0][2] = z1
  //   filteredSafeWaypoints[filteredSafeWaypoints.length - 1][2] = z2
  //   // Re-calculate minClearance after smoothing (more accurate)
  //   minClearance = Infinity
  //   for (let i = 0; i < filteredSafeWaypoints.length; i++) {
  //     const actual = filteredSafeWaypoints[i][2] - zTerrainList[i]
  //     if (actual < minClearance) minClearance = actual
  //   }

  //   // Compute pitch attitude
  //   const pitchDeg = []
  //   for (let i = 0; i < filteredSafeWaypoints.length - 1; i++) {
  //     const pitch = computePitch(filteredSafeWaypoints[i], filteredSafeWaypoints[i + 1])
  //     pitchDeg.push(pitch)
  //   }
  //   pitchDeg.push(pitchDeg[pitchDeg.length - 1] || 0)

  //   return {
  //     waypoints: filteredSafeWaypoints,
  //     lonlats,
  //     pitchDeg,
  //     distance: totalDist,
  //     maxTerrain: maxTerrain, // Number(maxTerrain.toFixed(1)),
  //     minClearance: minClearance, // Number(minClearance.toFixed(2)),
  //     requestedClearance: clearance,
  //     isDirect: false
  //   }

  //   // return {
  //   //   safeWaypoints, // [[x, y, z], ...]
  //   //   lonlats,
  //   //   pitchDeg, // pitch in degrees (positive = climb, negative = descend)
  //   //   distance: totalDist,
  //   //   maxTerrain: maxTerrain,
  //   //   minClearance: minClearance, // ← This is the key safety number
  //   //   requestedClearance: clearance
  //   // }
  // }

  // function generateSafeDronePath (start, end, root, clearance = 8.0, samplingInterval = 3.0) {
  //   const [x1, y1, z1] = start
  //   const [x2, y2, z2] = end

  //   const dx = x2 - x1
  //   const dy = y2 - y1
  //   const totalDist = Math.hypot(dx, dy)

  //   if (totalDist === 0) return { waypoints: [start], pitchDeg: [0], distance: 0 }

  //   const numSamples = Math.ceil(totalDist / samplingInterval) + 1
  //   const waypoints = []
  //   const lonlats = []
  //   const zTerrainList = []
  //   let maxTerrain = -Infinity

  //   // First pass: collect terrain and compute raw safe z
  //   for (let i = 0; i < numSamples; i++) {
  //     const t = i / (numSamples - 1)
  //     const x = x1 + t * dx
  //     const y = y1 + t * dy

  //     const pixel = root?.$map.getPixelFromCoordinate([x, y])
  //     let terrainAlt = 0
  //     if (pixel && root?.$demLayer) {
  //       const data = root?.$demLayer.getData(pixel)
  //       if (data && data.length > 0) terrainAlt = data[0]
  //     }

  //     const zLinear = z1 + t * (z2 - z1)
  //     const zMinSafe = terrainAlt + clearance
  //     const zSafe = Math.max(zLinear, zMinSafe)

  //     waypoints.push([x, y, zSafe])
  //     lonlats.push(toLonLat([x, y]))
  //     zTerrainList.push(terrainAlt)
  //     maxTerrain = Math.max(maxTerrain, terrainAlt)
  //   }

  //   // Smoothing: use a forward-looking "lazy descent" + moving max
  //   // This prevents descending too early or too late when end is lower
  //   const window = Math.max(5, Math.floor(numSamples * 0.06))
  //   for (let i = 0; i < waypoints.length; i++) {
  //     let maxNeeded = waypoints[i][2]
  //     for (let j = i; j < Math.min(waypoints.length, i + window); j++) {
  //       maxNeeded = Math.max(maxNeeded, zTerrainList[j] + clearance)
  //     }
  //     waypoints[i][2] = Math.max(waypoints[i][2], maxNeeded)
  //   }

  //   // Force exact start and end
  //   waypoints[0][2] = z1
  //   waypoints[waypoints.length - 1][2] = z2

  //   // Compute pitch (negative = nose down during descent)
  //   const pitchDeg = []
  //   for (let i = 0; i < waypoints.length - 1; i++) {
  //     const dz = waypoints[i + 1][2] - waypoints[i][2]
  //     const ds = Math.hypot(
  //       waypoints[i + 1][0] - waypoints[i][0],
  //       waypoints[i + 1][1] - waypoints[i][1]
  //     )
  //     const slope = ds > 0 ? dz / ds : 0
  //     const pitch = Math.atan(slope) * (180 / Math.PI)
  //     pitchDeg.push(pitch)
  //   }
  //   pitchDeg.push(pitchDeg[pitchDeg.length - 1] || 0)

  //   return {
  //     waypoints,
  //     lonlats,
  //     pitchDeg,
  //     distance: totalDist,
  //     maxTerrain,
  //     minClearance: Math.min(...waypoints.map((wp, i) => wp[2] - zTerrainList[i]))
  //   }
  // }

  function calculateTurnDamping (p1:number[], p2:number[], speed:number = 6):number {
    const xdiff = p2[0] - p1[0]; const ydiff = p2[1] - p1[1]; const zdiff = p2[2] - p1[2]
    const segmentDistance = Math.sqrt(xdiff ** 2 + ydiff ** 2 + zdiff ** 2)

    // === Main logic to choose damping distance ===
    let damping: number

    if (segmentDistance < 30) {
      damping = Math.min(5, segmentDistance * 0.2) // very short segment → keep small
    } else {
      // Base on speed
      if (speed <= 6) damping = 6
      else if (speed <= 10) damping = 10 // your original example value
      else if (speed <= 14) damping = 15
      else damping = 20

      // Make it proportional to segment length (never too aggressive)
      const percentBased = segmentDistance * 0.25 // 25% of segment is usually very safe
      damping = Math.min(damping, percentBased)

      // Safety cap: never exceed 40% of segment on one side
      damping = Math.min(damping, segmentDistance * 0.4)
    }

    // Round to 1 decimal (DJI accepts float)
    damping = Math.round(damping * 10) / 10

    // Check constraint
    const maxSafe = segmentDistance * 0.8 // leave some margin
    if (damping * 2 > maxSafe) {
      damping = Math.floor(maxSafe / 2)
    }

    return damping
  }

  // Generate route with bearing
  function generateRouteWithBearing (
    flightParams: FlightParams,
    polygon: Polygon,
    routePoints: Point[],
    bearingDeg: number
  ): RouteBearing {
    const surfaceFollowModeEnable = flightParams.surfaceFollowModeEnable
    const safeClearance = flightParams.safeClearance
    const flightAltitude = flightParams.flightAltitude
    const waypointSpeed = flightParams.waypointSpeed

    const rotated = rotateRoutePoints(polygon, routePoints, bearingDeg)

    const routePointBearings:RoutePoint[] = []
    let pbPrev:any
    let pointIndex = 0

    for (let i = 0; i < rotated.length; i++) {
      const p = rotated[i]
      const pLonLat = toLonLat(p)

      let bearing = 0
      if (i < rotated.length - 1) bearing = calculateBearing(pLonLat, toLonLat(rotated[i + 1]))
      else if (i > 0) bearing = calculateBearing(toLonLat(rotated[i - 1]), pLonLat)

      if (bearing > 180) bearing = bearing - 360

      let altitude:number = 0
      if (surfaceFollowModeEnable === true) {
        const alt = getAltitudeFromCoordinate(p)
        if (alt !== null) {
          altitude = alt + flightAltitude
        }
      } else {
        altitude = flightAltitude
      }

      p[2] = altitude

      const turnDamping = (i > 0) ? calculateTurnDamping(pbPrev, p) : 0
      const new_pb = { index: pointIndex, coordinate: p, lonlat: pLonLat, altitude: altitude, point: p, bearing: bearing, turnDamping: turnDamping, speed: waypointSpeed }

      let safeFlightPath:any = { isDirect: true }
      if (surfaceFollowModeEnable === true) {
        safeFlightPath = (i > 0)
          ? generateSafeDronePath(
            pbPrev.coordinate,
            p,
            root,
            safeClearance,
            1,
            1
          )
          : { isDirect: true }
      }

      if (safeFlightPath.isDirect) {
        routePointBearings.push(new_pb)
        pointIndex++
      } else {
        for (let j = 0; j < safeFlightPath.waypoints?.length - 1; j++) {
          const point = safeFlightPath.waypoints[j]
          const lonlat = safeFlightPath.lonlats[j]
          const safe_pb = {
            index: pointIndex,
            coordinate: [point[0], point[1]],
            lonlat: lonlat,
            altitude: point[2],
            point: point,
            bearing: pbPrev.bearing,
            turnDamping: pbPrev.turnDamping,
            speed: waypointSpeed
          }
          routePointBearings.push(safe_pb)
          pointIndex++
        }
      }

      pbPrev = new_pb
    }

    return {
      flightDirectionDeg: routePointBearings[0].bearing,
      globalShootHeight: flightAltitude,
      surfaceRelativeHeight: flightAltitude,
      boundaryPolygon: polygon,
      routePointBearings: routePointBearings,
      info: {
        duration: 0,
        length: 0
      },
      gsd: {
        width: 0,
        height: 0
      },
      footprint: {
        width: 0,
        height: 0
      },
      flightParams: flightParams
    }
  }

  function rotateCoord (
    coord: number[],
    angleRad: number,
    origin: number[]
  ): number[] {
    const cos = Math.cos(angleRad)
    const sin = Math.sin(angleRad)

    const dx = coord[0] - origin[0]
    const dy = coord[1] - origin[1]

    return [
      origin[0] + dx * cos - dy * sin,
      origin[1] + dx * sin + dy * cos,
    ]
  }

  function rotatePolygon (
    polygon: Polygon,
    angleRad: number
  ): Polygon {
    const extent = polygon.getExtent()
    const origin = getCenter(extent)

    const rotatedCoords = polygon
      .getCoordinates()[0]
      .map(c => rotateCoord(c, angleRad, origin))

    return new Polygon([rotatedCoords])
  }

  function intersectHorizontalLine (
    y: number,
    ring: number[][]
  ): number[] {
    const xs: number[] = []

    for (let i = 0; i < ring.length - 1; i++) {
      const p1 = ring[i]
      const p2 = ring[i + 1]

      if (
        (p1[1] <= y && p2[1] > y) ||
            (p2[1] <= y && p1[1] > y)
      ) {
        const t = (y - p1[1]) / (p2[1] - p1[1])
        xs.push(p1[0] + t * (p2[0] - p1[0]))
      }
    }

    return xs.sort((a, b) => a - b)
  }

  function sampleAlongLine (
    length: number,
    start: number[],
    end: number[],
    step: number
  ): number[][] {
    const points: number[][] = []
    if (length > step) {
      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const length = Math.hypot(dx, dy)

      const ux = dx / length
      const uy = dy / length

      // const lengthn = length - step
      // for (let d = step; d < lengthn; d += step) {
      //   points.push([
      //     start[0] + ux * d,
      //     start[1] + uy * d
      //   ])
      // }

      points.push([
        start[0] + ux * step,
        start[1] + uy * step
      ])

      points.push([
        end[0],
        end[1]
      ])
    }

    return points
  }

  // function generateLawnmowerSegments (
  //   polygon: Polygon,
  //   lineSpacing: number,
  //   photoSpacing: number,
  //   flightPathDirectionDeg: number
  // ): LineSegment[] {
  //   const angleRad = (flightPathDirectionDeg * Math.PI) / 180

  //   // 1. Rotate polygon so scan lines are horizontal
  //   const rotated = rotatePolygon(polygon, -angleRad)
  //   const ring = rotated.getCoordinates()[0]

  //   const extent = rotated.getExtent()
  //   const minY = extent[1]
  //   const maxY = extent[3]

  //   const origin = getCenter(polygon.getExtent())

  //   const segments: LineSegment[] = []
  //   let reverse = false

  //   // 2. Horizontal sweep
  //   for (let y = minY; y <= maxY; y += photoSpacing) {
  //     const xs = intersectHorizontalLine(y, ring)

  //     for (let i = 0; i < xs.length; i += lineSpacing) {
  //       if (i + 1 >= xs.length) continue

  //       let start = [xs[i], y]
  //       let end = [xs[i + 1], y]

  //       if (reverse) {
  //         [start, end] = [end, start]
  //       }

  //       // rotate back to world coordinates
  //       segments.push({
  //         start: rotateCoord(start, angleRad, origin),
  //         end: rotateCoord(end, angleRad, origin),
  //       })
  //     }

  //     reverse = !reverse
  //   }

  //   return segments
  // }

  // function generateLawnmowerPathOLSegments (polygon: Polygon, flightPathDirectionDeg: number):Point[] {
  //   const footprint = calculateFootprint()

  //   const lineSpacing = footprint.width
  //   const photoSpacing = footprint.height

  //   const pathSegments = generateLawnmowerSegments(
  //     polygon,
  //     lineSpacing,
  //     photoSpacing,
  //     flightPathDirectionDeg
  //   )

  //   const waypoints: Point[] = []

  //   for (const seg of pathSegments) {
  //     const samples = sampleAlongLine(
  //       seg.start,
  //       seg.end,
  //       lineSpacing
  //     )

  //     for (const c of samples) {
  //       waypoints.push(new Point(c))
  //     }
  //   }

  //   return waypoints
  // }

  function calculateDistanceCoord (coord1:number[], coord2:number[]) {
    return Math.sqrt(
      (
        (coord1[0] - coord2[0]) * (coord1[0] - coord2[0])
      ) + (
        (coord1[1] - coord2[1]) * (coord1[1] - coord2[1])
      )
    )
  }

  // function calculateDistancePoints (p1:Point, p2:Point): number {
  //   const coord1 = p1.getCoordinates()
  //   const coord2 = p2.getCoordinates()
  //   return calculateDistanceCoord(coord1, coord2)
  // }

  function generateLawnmowerPath (
    flightParams: FlightParams,
    polygon: Polygon,
    flightPathDirectionDeg: number,
    cameraBearingAngleDeg: number,
    gsd: FlightWH,
    footprint:FlightWH
  ): RouteBearing {
    const angleRad = (flightPathDirectionDeg * Math.PI) / 180

    // 1. Rotate polygon so scan lines are horizontal
    const rotated = rotatePolygon(polygon, -angleRad)
    const ring = rotated.getCoordinates()[0]

    const extent = rotated.getExtent()
    const minY = extent[1]
    const maxY = extent[3]

    const origin = getCenter(polygon.getExtent())

    const points: Point[] = []

    let reverse = false

    let totalLength = 0
    // 2. Horizontal sweep
    for (let y = minY; y <= maxY; y += footprint.height) {
      const xs = intersectHorizontalLine(y, ring)

      for (let i = 0; i < xs.length; i += 2) {
        if (i + 1 >= xs.length) continue

        const a = [xs[i], y]
        const b = [xs[i + 1], y]

        const segment = reverse ? [b, a] : [a, b]

        let rotatedBackPrev:number[] = []

        for (const c of segment) {
          const rotatedBack = rotateCoord(c, angleRad, origin)
          if (rotatedBackPrev.length > 0) {
            const length = calculateDistanceCoord(rotatedBackPrev, rotatedBack)
            totalLength += length
            const segmentsAlongLine = sampleAlongLine(
              length,
              rotatedBackPrev,
              rotatedBack,
              footprint.width
            )
            if (segmentsAlongLine.length > 1) {
              const rotatedBackPushFlag = true
              // for (const segmentAlongLine of segmentsAlongLine) {
              //   const segmentPoint = new Point(segmentAlongLine)
              //   points.push(segmentPoint)
              //   if (rotatedBackPushFlag) {
              //     rotatedBackPushFlag = calculateDistanceCoord(rotatedBack, segmentAlongLine) <= footprint.width
              //   }
              // }
              if (rotatedBackPushFlag) {
                points.push(new Point(rotatedBack))
              }
            }
          } else {
            points.push(new Point(rotatedBack))
          }
          rotatedBackPrev = rotatedBack
        }
      }

      reverse = !reverse
    }

    const routeBearing = generateRouteWithBearing(
      flightParams, polygon, points, cameraBearingAngleDeg
    )

    routeBearing.footprint = footprint
    routeBearing.gsd = gsd
    routeBearing.flightParams = flightParams
    routeBearing.info = {
      length: totalLength,
      duration: totalLength / flightParams.waypointSpeed
    }
    return routeBearing
  }

  return {
    generateLawnmowerPath,
    calculateGsd,
    calculateFootprint
  }
}
